/** Console primitives: buttons, pills, cards, stats, tabs, fields. Colour lives in dots and icons only. */
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Icon } from "./Icon";
import { ago, initials, tone } from "./format";
import { useInTip, useTip } from "./tip";
import { useDocumentTitle } from "./title";

type BtnProps = React.ButtonHTMLAttributes<HTMLButtonElement> & { kind?: "primary" | "danger" | "ghost"; size?: "sm"; icon?: string; busy?: boolean; iconOnly?: boolean };

/**
 * Refresh: reads again now. It spins while the read runs (at least 700 ms,
 * so a fast answer still reads as done) and its hint says when the data was
 * last read.
 */
export function RefreshButton({ onRefresh, updatedAt, label = "Refresh" }: { onRefresh: () => Promise<unknown> | void; updatedAt?: number; label?: string }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (busy) return;
    setBusy(true);
    const started = Date.now();
    try {
      await onRefresh();
    } finally {
      setTimeout(() => setBusy(false), Math.max(0, 700 - (Date.now() - started)));
    }
  };
  const read = updatedAt ? ` · read ${ago(new Date(updatedAt).toISOString())}` : "";
  return <Button iconOnly icon="arrow-sync" aria-label={label} title={`${label}${read}`} busy={busy} onClick={run} />;
}

/** An icon-only button names itself in a tooltip (its title, else its aria-label) unless it already sits in a Tip. */
export function Button({ kind, size, icon, busy, iconOnly, className, children, disabled, title, ...rest }: BtnProps) {
  const cls = ["btn", kind, size, iconOnly ? "icon" : "", className].filter(Boolean).join(" ");
  const inTip = useInTip();
  const tipText = iconOnly && !inTip ? (title ?? rest["aria-label"]) : undefined;
  const { bind, node } = useTip(tipText);
  const on = tipText
    ? {
        onMouseEnter: (e: React.MouseEvent<HTMLButtonElement>) => (bind.onMouseEnter(e), rest.onMouseEnter?.(e)),
        onMouseLeave: (e: React.MouseEvent<HTMLButtonElement>) => (bind.onMouseLeave(), rest.onMouseLeave?.(e)),
        onFocus: (e: React.FocusEvent<HTMLButtonElement>) => (bind.onFocus(e), rest.onFocus?.(e)),
        onBlur: (e: React.FocusEvent<HTMLButtonElement>) => (bind.onBlur(), rest.onBlur?.(e)),
        onMouseDown: (e: React.MouseEvent<HTMLButtonElement>) => (bind.onMouseDown(), rest.onMouseDown?.(e)),
      }
    : {};
  return (
    <button type="button" className={cls} disabled={disabled || busy} aria-busy={busy || undefined} title={tipText ? undefined : title} {...rest} {...on}>
      {busy ? <Icon name="arrow-sync" className="spin" size={15} /> : icon ? <Icon name={icon} size={15} /> : null}
      {!iconOnly && children}
      {node}
    </button>
  );
}

/**
 * One line of supporting text: it ends in an ellipsis when it does not fit,
 * and then carries its full text as a hover title. `wrap` lets it run on.
 */
export function Line({ as: Tag = "p", className, style, wrap, children }: { as?: "p" | "div"; className?: string; style?: React.CSSProperties; wrap?: boolean; children: ReactNode }) {
  const ref = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || wrap) return;
    const read = () => {
      const cut = el.scrollWidth > el.clientWidth + 1;
      if (cut) el.title = el.textContent ?? "";
      else el.removeAttribute("title");
    };
    read();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  });
  const cls = [className, wrap ? "" : "oneline"].filter(Boolean).join(" ") || undefined;
  return (
    <Tag ref={ref as React.RefObject<HTMLParagraphElement & HTMLDivElement>} className={cls} style={style}>
      {children}
    </Tag>
  );
}

export type Tone = "ok" | "am" | "rd" | "bl" | "none";

export function Dot({ tone: t = "none", live }: { tone?: Tone; live?: boolean }) {
  return <span className={live ? "live" : `dot ${t === "none" ? "" : t}`} />;
}

export function Pill({ tone: t, children, title }: { tone?: Tone; children: ReactNode; title?: string }) {
  return (
    <span className="pill" title={title}>
      {t && <Dot tone={t} />}
      {children}
    </span>
  );
}

export function Chip({ children, dashed, icon, title }: { children: ReactNode; dashed?: boolean; icon?: string; title?: string }) {
  return (
    <span className={`chip${dashed ? " dashed" : ""}`} title={title}>
      {icon && <Icon name={icon} size={12} />}
      {children}
    </span>
  );
}

export function Avatar({ name, size, round, color }: { name: string; size?: "sm"; round?: boolean; color?: string }) {
  return (
    <span className={`avatar${size ? ` ${size}` : ""}${round ? " round" : ""}`} style={{ background: color ?? tone(name) }} aria-hidden>
      {initials(name)}
    </span>
  );
}

export function Card({ title, sub, actions, children, footer, className, style, pad = true, wrap }: { title?: ReactNode; sub?: ReactNode; actions?: ReactNode; children?: ReactNode; footer?: ReactNode; className?: string; style?: React.CSSProperties; pad?: boolean; wrap?: boolean }) {
  return (
    <section className={`card ${className ?? ""}`} style={style}>
      {(title || actions) && (
        <div className="hd">
          <div className="grow">
            {title && <h3>{title}</h3>}
            {sub && <Line wrap={wrap}>{sub}</Line>}
          </div>
          {actions && <div className="actions">{actions}</div>}
        </div>
      )}
      {pad ? <div className="bd">{children}</div> : children}
      {footer && <div className="ft">{footer}</div>}
    </section>
  );
}

export function Stat({ icon, label, value, unit, foot, children, onClick }: { icon?: string; label: string; value: ReactNode; unit?: ReactNode; foot?: ReactNode; children?: ReactNode; onClick?: () => void }) {
  return (
    <div className={`stat${onClick ? " click" : ""}`} onClick={onClick} role={onClick ? "link" : undefined} tabIndex={onClick ? 0 : undefined} onKeyDown={onClick ? (e) => e.key === "Enter" && onClick() : undefined}>
      <div className="lb">
        {icon && <Icon name={icon} size={14} />}
        {label}
      </div>
      <div className="v">
        {value}
        {unit && <small>{unit}</small>}
      </div>
      {children}
      {foot && <div className="ft">{foot}</div>}
    </div>
  );
}

/** A segmented bar; parts are fractions of total. */
export function Bar({ parts, total, height = 6 }: { parts: { value: number; tone?: "am" | "rd" | "ok" | "lt" | "" }[]; total: number; height?: number }) {
  return (
    <div className="bar" style={{ height }}>
      {parts.map((p, i) => (
        <i key={i} className={p.tone ?? ""} style={{ width: `${total > 0 ? Math.min(100, (p.value / total) * 100) : 0}%` }} />
      ))}
    </div>
  );
}

export function Empty({ icon = "search", title, children, action }: { icon?: string; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <div className="tile">
        <Icon name={icon} size={22} />
      </div>
      <h4>{title}</h4>
      {children && <div style={{ maxWidth: 440, margin: "0 auto" }}>{children}</div>}
      {action && <div style={{ marginTop: 14 }}>{action}</div>}
    </div>
  );
}

export function Skeleton({ rows = 4, height = 18 }: { rows?: number; height?: number }) {
  return (
    <div className="col" style={{ padding: 18, gap: 12 }} aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skel" style={{ height, width: `${92 - (i % 3) * 14}%`, animationDelay: `${i * 70}ms` }} />
      ))}
    </div>
  );
}

/** The first paint of a signed-in console: the mark and a quiet progress line, or what went wrong. */
export function Boot({ error, onRetry }: { error?: Error | null; onRetry?: () => void }) {
  return (
    <div className="boot" aria-busy={!error} aria-live="polite">
      <div>
        <img src="/brand/inlinea-mark.svg" alt="" />
        {error ? (
          <>
            <b style={{ fontSize: 15 }}>The console could not load</b>
            <span className="muted">{error.message}</span>
            {onRetry && <Button onClick={onRetry} icon="arrow-clockwise">Try again</Button>}
          </>
        ) : (
          <span className="track" aria-label="Loading" />
        )}
      </div>
    </div>
  );
}

export function ErrorNote({ error, onRetry }: { error: Error | null; onRetry?: () => void }) {
  if (!error) return null;
  return (
    <div className="banner red" role="alert">
      <Icon name="error-circle" style={{ color: "var(--rd)" }} />
      <span className="grow">{error.message}</span>
      {onRetry && (
        <Button size="sm" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

export function Banner({ tone: t, icon, children, action }: { tone?: "amber" | "red" | "green" | "blue"; icon?: string; children: ReactNode; action?: ReactNode }) {
  const color = t === "amber" ? "var(--am)" : t === "red" ? "var(--rd)" : t === "green" ? "var(--gn)" : "var(--bl)";
  return (
    <div className={`banner ${t ?? ""}`}>
      {icon && <Icon name={icon} size={18} style={{ color }} />}
      <div className="grow">{children}</div>
      {action}
    </div>
  );
}

/** Where the selected child sits inside its strip, so a single marker can slide to it. */
function useMarker(deps: unknown[]) {
  const box = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<{ left: number; top: number; width: number; height: number } | null>(null);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const read = () => {
      const on = el.querySelector<HTMLElement>("button.on");
      setAt(on ? { left: on.offsetLeft, top: on.offsetTop, width: on.offsetWidth, height: on.offsetHeight } : null);
    };
    read();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  return [box, at] as const;
}

export function Tabs<T extends string>({ tabs, value, onChange }: { tabs: { id: T; label: string; count?: number }[]; value: T; onChange: (v: T) => void }) {
  const [box, at] = useMarker([value, tabs.map((t) => `${t.id}${t.count ?? ""}`).join()]);
  return (
    <div className={`tabs${at ? " slide" : ""}`} role="tablist" ref={box}>
      {tabs.map((t) => (
        <button key={t.id} role="tab" aria-selected={value === t.id} className={value === t.id ? "on" : ""} onClick={() => onChange(t.id)}>
          {t.label}
          {t.count !== undefined && <span className="n">{t.count}</span>}
        </button>
      ))}
      {at && <i style={{ left: at.left, width: at.width }} />}
    </div>
  );
}

export function Seg<T extends string | number>({ options, value, onChange, disabled }: { options: [T, string][]; value: T; onChange: (v: T) => void; disabled?: boolean }) {
  const [box, at] = useMarker([value, options.map((o) => o[1]).join()]);
  return (
    <div className={`seg${at ? " slide" : ""}`} role="radiogroup" ref={box}>
      {at && <i style={at} />}
      {options.map(([v, l]) => (
        <button key={String(v)} type="button" role="radio" aria-checked={v === value} className={v === value ? "on" : ""} onClick={() => !disabled && onChange(v)} disabled={disabled}>
          {l}
        </button>
      ))}
    </div>
  );
}

/** A wizard's steps: done ones are links back, the current one is filled, later ones wait. */
export function Steps({ steps, current, onStep }: { steps: string[]; current: number; onStep?: (i: number) => void }) {
  return (
    <ol className="steps" style={{ listStyle: "none", margin: "0 0 18px", paddingLeft: 2 }}>
      {steps.map((s, i) => (
        <li key={s} style={{ display: "contents" }}>
          {i > 0 && <span className={i <= current ? "done" : ""} aria-hidden />}
          <button type="button" className={`step${i === current ? " on" : i < current ? " done" : ""}`} aria-current={i === current ? "step" : undefined} disabled={i > current} onClick={() => i < current && onStep?.(i)}>
            <i>{i < current ? <Icon name="checkmark" size={12} /> : i + 1}</i>
            {s}
          </button>
        </li>
      ))}
    </ol>
  );
}

/** A password field with a show or hide control and a Caps Lock hint. */
export function PasswordInput({ id, name, value, onChange, autoComplete = "current-password", required, autoFocus, className }: { id?: string; name?: string; value?: string; onChange?: (v: string) => void; autoComplete?: string; required?: boolean; autoFocus?: boolean; className?: string }) {
  const [show, setShow] = useState(false);
  const [caps, setCaps] = useState(false);
  const key = (e: React.KeyboardEvent) => setCaps(e.getModifierState?.("CapsLock") ?? false);
  return (
    <>
      <div className="inwrap">
        <input id={id} name={name} className={`input ${className ?? ""}`} type={show ? "text" : "password"} autoComplete={autoComplete} required={required} autoFocus={autoFocus} spellCheck={false} {...(onChange ? { value: value ?? "", onChange: (e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value) } : {})} onKeyDown={key} onKeyUp={key} onBlur={() => setCaps(false)} />
        <button type="button" aria-label="Show password" aria-pressed={show} title={show ? "Hide password" : "Show password"} onClick={() => setShow((x) => !x)}>
          <Icon name={show ? "eye-off" : "eye"} size={16} />
        </button>
      </div>
      {caps && (
        <span className="hint row" style={{ color: "var(--am)", gap: 6 }} aria-live="polite">
          <Icon name="keyboard-shift-uppercase" size={13} /> Caps Lock is on.
        </span>
      )}
    </>
  );
}

export function Switch({ on, onChange, disabled, label }: { on: boolean; onChange: (v: boolean) => void; disabled?: boolean; label: string }) {
  return <button type="button" role="switch" aria-checked={on} aria-label={label} className={`switch${on ? " on" : ""}`} disabled={disabled} onClick={() => onChange(!on)} />;
}

export function Field({ label, hint, error, children, htmlFor }: { label: ReactNode; hint?: ReactNode; error?: string | null; children: ReactNode; htmlFor?: string }) {
  return (
    <div className="field">
      <label htmlFor={htmlFor}>{label}</label>
      {children}
      {error ? <span className="err">{error}</span> : hint ? <span className="hint">{hint}</span> : null}
    </div>
  );
}

/** A settings row: label and one-line description left, control right. */
export function SettingRow({ title, desc, children, last, wrap }: { title: ReactNode; desc?: ReactNode; children: ReactNode; last?: boolean; wrap?: boolean }) {
  return (
    <div className="row setrow" style={{ padding: "14px 18px", borderTop: "1px solid var(--ln2)", gap: 24, alignItems: "flex-start", ...(last ? {} : {}) }}>
      <div className="grow" style={{ minWidth: 200 }}>
        <div style={{ fontWeight: 500 }}>{title}</div>
        {desc && <Line as="div" wrap={wrap} className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>{desc}</Line>}
      </div>
      <div style={{ flex: "0 1 auto", minWidth: 0, display: "flex", justifyContent: "flex-end" }}>{children}</div>
    </div>
  );
}

export function Facts({ rows }: { rows: [ReactNode, ReactNode][] }) {
  return (
    <table className="facts">
      <tbody>
        {rows.map(([k, v], i) => (
          <tr key={i}>
            <td>{k}</td>
            <td>{v ?? "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function Copy({ text, label = "Copy" }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="btn ghost sm icon"
      aria-label={label}
      title={done ? "Copied" : label}
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(text).then(() => {
          setDone(true);
          setTimeout(() => setDone(false), 1400);
        });
      }}
    >
      <Icon name={done ? "checkmark" : "copy"} size={14} />
    </button>
  );
}

/** The page's heading. Its title also names the page in the browser tab (title.ts). */
export function PageHeader({ title, lead, actions, icon, wrap }: { title: string; lead?: ReactNode; actions?: ReactNode; icon?: string; wrap?: boolean }) {
  useDocumentTitle(title);
  return (
    <div className="ph">
      {icon && (
        <div className="tile">
          <Icon name={icon} size={22} />
        </div>
      )}
      <div className="grow">
        <h1>{title}</h1>
        {lead && <Line wrap={wrap}>{lead}</Line>}
      </div>
      {actions && <div className="actions">{actions}</div>}
    </div>
  );
}

export function healthTone(h: string | null | undefined): Tone {
  return h === "healthy" || h === "operational" || h === "ok" || h === "succeeded" || h === "upgraded" || h === "online" || h === "active" || h === "connected" || h === "sent" || h === "done"
    ? "ok"
    : h === "warning" || h === "degraded" || h === "rolled_back" || h === "needs_you" || h === "waiting" || h === "maintenance" || h === "offline" || h === "invited" || h === "setting_up" || h === "dry_run"
      ? "am"
      : h === "critical" || h === "down" || h === "failed" || h === "outage" || h === "suspended" || h === "failing" || h === "refused"
        ? "rd"
        : h === "running" || h === "upgrading" || h === "provisioning" || h === "queued" || h === "new" || h === "open"
          ? "bl"
          : "none";
}
